import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Text from "../../components/typography/Text";
import CustomRadioButton from "../../components/common/CustomRadioButton/CustomRadioButton";
import CustomDatePicker from "../../components/common/CustomDatePicker/CustomDatePicker";
import CustomPaperBox from "../../components/common/CustomPaperBox";
import CustomButton from "../../components/common/CustomButton/CustomButton";
import { toast } from "react-toastify";
import { updateCourse } from "../../utils/urls";

const validationSchema = Yup.object().shape({
    access_for_all_user: Yup.boolean().required(),
    access_for_verified_user: Yup.boolean().required(),
    availability_schedule: Yup.string().required("Schedule type is required"),
    schedule_date: Yup.string()
        .nullable()
        .when("availability_schedule", {
            is: "Schedule",
            then: (schema) => schema.required("Date is required"),
        }),
});

const EditAccessTab = ({ courseData, onSubmit }) => {
    const sub_category_id = (courseData?.sub_category_id || []).map((sc) => sc.sub_category_id);

    const initialFormData = {
        access_for_all_user: courseData?.access_for_all_user ?? true,
        access_for_verified_user: courseData?.access_for_verified_user ?? false,
        availability_schedule: courseData?.availability_schedule ?? "Immediate",
        schedule_date:
            courseData?.availability_schedule === "Schedule"
                ? courseData?.schedule_date ?? "Immediate"
                : "Immediate",
    };

    const [accessDataLocal, setAccessDataLocal] = useState(initialFormData);
    const [loadingStatus, setLoadingStatus] = useState(null);

    const handleUpdate = async (values, action) => {
        try {
            setLoadingStatus(action);
            const payload = new FormData();
            payload.append("id", courseData?.id);
            payload.append("access_for_all_user", values.access_for_all_user);
            payload.append("access_for_verified_user", values.access_for_verified_user);
            payload.append("availability_schedule", values.availability_schedule);
            if (values.availability_schedule === "Schedule") {
                payload.append("schedule_date", values.schedule_date || "Immediate");
            }
            payload.append("status", action === "save" ? "Saved As Draft" : "Published");
            payload.append("title", courseData?.title);
            payload.append("description", courseData?.description);
            payload.append("category_id", courseData?.category_id?.category_id || courseData?.category_id);
            payload.append("sub_category_id", sub_category_id.join(","));

            const response = await axios.put(updateCourse, payload);

            if (response.data?.success) {
                toast.success("Course access settings updated successfully.");
                if (onSubmit) onSubmit();
            } else {
                toast.error("Failed to update course access settings.");
            }
        } catch (error) {
            console.error("Update error:", error);
            toast.error("Something went wrong while updating access settings.");
        } finally {
            setLoadingStatus(null);
        }
    };

    return (
        <div className="w-full">
            <CustomPaperBox className="!pt-0">
                <Formik
                    enableReinitialize
                    initialValues={accessDataLocal}
                    validationSchema={validationSchema}
                    onSubmit={(values) => handleUpdate(values, "")}
                >
                    {({ values, setFieldValue }) => (
                        <Form className="p-6 space-y-6">
                            {/* Access switches */}
                            <div className="flex justify-start">
                                <div className="flex items-center space-x-2 w-1/3">
                                    <Text className="!font-bold">All Users</Text>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={values.access_for_all_user}
                                            onChange={() => {
                                                setFieldValue("access_for_all_user", true);
                                                setFieldValue("access_for_verified_user", false);
                                                setAccessDataLocal((prev) => ({
                                                    ...prev,
                                                    access_for_all_user: true,
                                                    access_for_verified_user: false,
                                                }));
                                            }}
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>

                                <div className="flex items-center space-x-2 w-1/3">
                                    <Text className="!font-bold">Verified Advisors</Text>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={values.access_for_verified_user}
                                            onChange={() => {
                                                setFieldValue("access_for_all_user", false);
                                                setFieldValue("access_for_verified_user", true);
                                                setAccessDataLocal((prev) => ({
                                                    ...prev,
                                                    access_for_all_user: false,
                                                    access_for_verified_user: true,
                                                }));
                                            }}
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>
                            </div>

                            {/* Availability schedule */}
                            <div className="flex justify-left items-center m-2">
                                <div className="w-1/3">
                                    <Text variant="label-18px" className="m-2 !font-bold">
                                        Availability Schedule
                                    </Text>
                                    <CustomRadioButton
                                        radioList={[
                                            { label: "Immediate", value: "Immediate" },
                                            { label: "Schedule", value: "Schedule" },
                                        ]}
                                        inRow={true}
                                        value={values.availability_schedule}
                                        onChange={(e) => {
                                            setFieldValue("availability_schedule", e.target.value);
                                            setAccessDataLocal((prev) => ({
                                                ...prev,
                                                availability_schedule: e.target.value,
                                                schedule_date:
                                                    e.target.value === "Immediate" ? "" : prev.schedule_date,
                                            }));
                                        }}
                                        name="availability_schedule"
                                    />
                                </div>

                                <div className="w-1/2">
                                    <CustomDatePicker
                                        label="Select Date"
                                        required={true}
                                        name="schedule_date"
                                        disabled={values.availability_schedule !== "Schedule"}
                                        onChange={(e) => {
                                            setFieldValue("schedule_date", e.target.value);
                                            setAccessDataLocal((prev) => ({
                                                ...prev,
                                                schedule_date: e.target.value,
                                            }));
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Submit button */}
                            <div className="flex justify-end gap-6 mt-4 lg:col-span-2">
                                <CustomButton
                                    label="Cancel"
                                    type="button"
                                    variant="outlined"
                                    onClick={() => onSubmit()}
                                />
                                <CustomButton
                                    label="Update"
                                    isLoading={loadingStatus === "update"}
                                    disabled={!!loadingStatus}
                                    type="submit"
                                    variant="contained"
                                    onClick={() => handleUpdate(values, "update")}
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            </CustomPaperBox>
        </div>
    );
};

export default EditAccessTab;